import axios from 'axios';
import { HttpError } from '@/errors/HttpError';
import qs from 'qs';
import { ACTING_TENANT_HEADER, clearActingTenant, readActingTenant } from '@/utils/actingTenant';

export const BFF_BASE_URL = process.env.REACT_APP_BFF_URL || 'http://127.0.0.1:8001';

const DEFAULT_TIMEOUT_MS = 35_000;

declare module 'axios' {
  interface AxiosRequestConfig {
    handlesForbidden?: boolean;
  }
}

const apiClient = axios.create({
  baseURL: BFF_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  paramsSerializer: params => {
    return qs.stringify(params, { arrayFormat: 'repeat' });
  },
});

type AuthEventDetail = { type: 'unauthorized' } | { type: 'forbidden' } | { type: 'auth_error' };

const AUTH_EVENT_NAME = 'measure-ease:auth-event';
const emitAuthEvent = (detail: AuthEventDetail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<AuthEventDetail>(AUTH_EVENT_NAME, { detail }));
};

apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('accessToken');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const actingTenant = readActingTenant();
    if (actingTenant) {
      config.headers[ACTING_TENANT_HEADER] = actingTenant;
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  response => response,
  error => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;

      if (status === 401) {
        localStorage.removeItem('accessToken');
        clearActingTenant();
        emitAuthEvent({ type: 'unauthorized' });
      } else if (status === 403 && !error.config?.handlesForbidden) {
        emitAuthEvent({ type: 'forbidden' });
      }

      return Promise.reject(
        new HttpError(error.message, status, data, { ...error.response.headers })
      );
    }

    // Network errors (CORS, offline, DNS, timeouts, BFF down)
    if (error.request) {
      return Promise.reject(
        new HttpError('Network error or server is unreachable', 0, { message: error.message })
      );
    }

    return Promise.reject(
      new HttpError('Failed to construct request', 0, { message: error.message })
    );
  }
);

export default apiClient;
